// Power Dialer — rep-driven progressive auto-dialer.
//   - Left column: current contact + notes summary + AI opener + hoursGate
//   - Right column: queue progress + disposition buttons + running tally
//   - Bottom: pause / resume / end session
//
// Session flow: launch from Segments (which writes activeDialerSessionId into
// sessionStorage) or from the "Start Ad-hoc" button here. We poll GET
// /sessions/:id after each disposition to advance the queue.
import React, { useEffect, useState, useRef } from 'react';
import { G, styles, api, fmtUsd } from '../theme.js';
import { Btn, Badge, Input, Select, Field, Spinner, EmptyState } from '../ui.jsx';

const DISPOSITIONS = [
  { code: 'booked', label: '✅ Booked', color: G.green, hot: true },
  { code: 'callback', label: '↩ Callback', color: G.blue },
  { code: 'voicemail', label: '📭 Voicemail', color: G.yellow },
  { code: 'no_answer', label: '📵 No Answer', color: G.textMuted },
  { code: 'not_interested', label: '👎 Not Interested', color: G.orange },
  { code: 'wrong_number', label: '❌ Wrong Number', color: G.red },
  { code: 'dnc', label: '🛑 DNC (opt out)', color: G.red },
];

export function PowerDialer({ toast }) {
  const [sessionId, setSessionId] = useState(() => window.sessionStorage.getItem('activeDialerSessionId') || null);
  const [state, setState] = useState(null);
  const [notes, setNotes] = useState('');
  const [savingDisp, setSavingDisp] = useState(false);
  const [segments, setSegments] = useState([]);
  const callStartRef = useRef(null);

  const refresh = () => {
    if (!sessionId) { setState(null); return; }
    api(`/api/power-dialer/sessions/${sessionId}`).then(setState).catch(e=>{
      toast(e.message, 'error');
      setSessionId(null);
      window.sessionStorage.removeItem('activeDialerSessionId');
    });
  };
  useEffect(() => { refresh(); }, [sessionId]);
  useEffect(() => { api('/api/segments').then(d=>setSegments(d.segments||[])).catch(()=>{}); }, []);
  useEffect(() => { callStartRef.current = Date.now(); }, [state?.currentContact?.id]);

  const startFromSegment = async (segmentId) => {
    try {
      const r = await api('/api/power-dialer/sessions', { method:'POST', body:{ segmentId }});
      window.sessionStorage.setItem('activeDialerSessionId', r.id);
      setSessionId(r.id);
      toast(`📞 Queued ${r.queueLength} contacts`);
    } catch (e) { toast(e.message, 'error'); }
  };

  const disposition = async (code) => {
    if (!state?.currentContact) return;
    setSavingDisp(true);
    try {
      const durationSec = callStartRef.current ? Math.floor((Date.now() - callStartRef.current)/1000) : null;
      await api(`/api/power-dialer/sessions/${sessionId}/disposition`, {
        method: 'POST',
        body: { outcome: code, notes: notes.trim() || null, duration_sec: durationSec },
      });
      setNotes(''); refresh();
    } catch (e) { toast(e.message, 'error'); }
    finally { setSavingDisp(false); }
  };

  const skip = async () => {
    if (!state?.currentContact) return;
    try { await api(`/api/power-dialer/sessions/${sessionId}/skip`, { method:'POST' }); refresh(); }
    catch (e) { toast(e.message, 'error'); }
  };
  const pauseResume = async () => {
    const endpoint = state.session.status === 'active' ? 'pause' : 'resume';
    try { await api(`/api/power-dialer/sessions/${sessionId}/${endpoint}`, { method:'POST' }); refresh(); }
    catch (e) { toast(e.message, 'error'); }
  };
  const endSession = async () => {
    if (!confirm('End this dialer session?')) return;
    try {
      await api(`/api/power-dialer/sessions/${sessionId}/end`, { method:'POST' });
      window.sessionStorage.removeItem('activeDialerSessionId');
      setSessionId(null);
      toast('Session ended');
    } catch (e) { toast(e.message, 'error'); }
  };

  // Launch screen — no active session
  if (!sessionId || !state) {
    return <div style={{ padding:'20px 24px', maxWidth:1100 }}>
      <h1 style={{ margin:0, fontSize:24, fontWeight:800 }}>📞 Power Dialer</h1>
      <div style={{ fontSize:13, color:G.textMuted, marginTop:4, marginBottom:20 }}>
        Rip through a queue with the AI opener + hours-gate + one-tap dispositions. Pick a segment to start.
      </div>
      {segments.length === 0 ? (
        <EmptyState icon="📇" title="No segments yet" body="Build a segment first (Segments tab) — the dialer runs off saved lists." ctaLabel="Go to Segments" onCta={()=>{ window.location.hash = '#segments'; window.dispatchEvent(new HashChangeEvent('hashchange')); }}/>
      ) : (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(280px, 1fr))', gap:12 }}>
          {segments.map(s => (
            <div key={s.id} style={{ ...styles.card, padding:16 }}>
              <strong style={{ fontSize:15 }}>{s.name} {s.is_favorite ? '⭐' : ''}</strong>
              {s.description && <div style={{ fontSize:12, color:G.textMuted, marginTop:4, marginBottom:8 }}>{s.description}</div>}
              <Btn v="primary" onClick={()=>startFromSegment(s.id)} style={{ marginTop:10, width:'100%' }}>📞 Start Dialing</Btn>
            </div>
          ))}
        </div>
      )}
    </div>;
  }

  const { session, progress, dispositions, currentContact, opener, hoursGate } = state;
  const queueDone = session.status === 'ended' || progress.remaining === 0;
  const gateBlocked = hoursGate && hoursGate.allowed === false;

  return <div style={{ padding:'20px 24px', maxWidth:1200 }}>
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14, gap:12, flexWrap:'wrap' }}>
      <div>
        <h1 style={{ margin:0, fontSize:22, fontWeight:800 }}>📞 {session.name || 'Power Dialer'}</h1>
        <div style={{ fontSize:12, color:G.textMuted, marginTop:2 }}>{progress.done} / {progress.total} · {progress.remaining} remaining</div>
      </div>
      <div style={{ display:'flex', gap:6 }}>
        <Badge c={session.status==='active'?G.green:G.yellow} bg={(session.status==='active'?G.green:G.yellow)+'22'}>{session.status}</Badge>
        {session.status !== 'ended' && <Btn sz="sm" onClick={pauseResume}>{session.status==='active'?'⏸ Pause':'▶ Resume'}</Btn>}
        <Btn sz="sm" v="danger" onClick={endSession}>End Session</Btn>
      </div>
    </div>

    <div style={{ height:6, background:G.input, borderRadius:3, overflow:'hidden', marginBottom:20 }}>
      <div style={{ height:'100%', width:`${progress.total ? (progress.done/progress.total)*100 : 0}%`, background:G.green, transition:'width .2s' }}/>
    </div>

    {queueDone ? (
      <div style={{ ...styles.card, padding:32, textAlign:'center' }}>
        <div style={{ fontSize:40, marginBottom:8 }}>🏁</div>
        <div style={{ fontSize:18, fontWeight:700, marginBottom:6 }}>Queue complete</div>
        <div style={{ fontSize:13, color:G.textMuted, marginBottom:14 }}>
          Booked: {dispositions?.booked || 0} · Callbacks: {dispositions?.callback || 0} · VMs: {dispositions?.voicemail || 0} · No answer: {dispositions?.no_answer || 0}
        </div>
        <Btn v="primary" onClick={()=>{ window.sessionStorage.removeItem('activeDialerSessionId'); setSessionId(null); }}>Start Another Session</Btn>
      </div>
    ) : !currentContact ? <Spinner sz={32}/> : (
      <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap:16 }}>
        {/* Current contact card */}
        <div style={{ ...styles.card, padding:20 }}>
          <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:14 }}>
            <div>
              <div style={{ fontSize:22, fontWeight:800 }}>{currentContact.name || 'Unnamed'}</div>
              <div style={{ fontSize:14, color:G.textMuted, marginTop:2 }}>{currentContact.stage} · {currentContact.source || currentContact.leadSource || 'Unknown source'}</div>
            </div>
            <a href={`tel:${currentContact.phone}`} style={{ background:G.green, color:'#fff', padding:'14px 22px', borderRadius:10, fontWeight:800, textDecoration:'none', fontSize:18 }}>
              📞 {currentContact.phone}
            </a>
          </div>

          {gateBlocked && (
            <div style={{ background:G.red+'22', border:`1px solid ${G.red}`, padding:12, borderRadius:8, marginBottom:14 }}>
              <div style={{ color:G.red, fontWeight:700, fontSize:13 }}>🚫 TCPA hours gate: {hoursGate.reason}</div>
              <div style={{ fontSize:12, color:G.textMuted, marginTop:4 }}>Local time: {hoursGate.localTime} ({hoursGate.tz})</div>
              <Btn sz="sm" style={{ marginTop:8 }} onClick={skip}>Skip this contact →</Btn>
            </div>
          )}

          {currentContact.address && <div style={{ fontSize:13, color:G.textMuted, marginBottom:10 }}>📍 {currentContact.address}</div>}

          {opener && (
            <div style={{ background:G.purpleDim, padding:12, borderRadius:8, marginBottom:14, border:`1px dashed ${G.purple}` }}>
              <div style={{ fontSize:11, color:G.purple, textTransform:'uppercase', fontWeight:700, marginBottom:4 }}>🤖 AI Opener</div>
              <div style={{ fontSize:14, lineHeight:1.5 }}>{opener}</div>
            </div>
          )}

          {currentContact.notesSummary && (
            <div style={{ background:G.input, padding:12, borderRadius:8, marginBottom:14 }}>
              <div style={{ fontSize:11, color:G.textMuted, textTransform:'uppercase', fontWeight:700, marginBottom:4 }}>Notes</div>
              <div style={{ fontSize:13, lineHeight:1.5, whiteSpace:'pre-wrap' }}>{currentContact.notesSummary}</div>
            </div>
          )}

          <Field label="Call notes (optional — saved with disposition)">
            <textarea value={notes} onChange={e=>setNotes(e.target.value)} rows={3}
              placeholder="What happened? Booked for Tuesday, wants a rough estimate, etc."
              style={{ ...styles.inp, minHeight:80, resize:'vertical', width:'100%' }}/>
          </Field>

          <div style={{ fontSize:11, color:G.textMuted, textTransform:'uppercase', fontWeight:700, marginBottom:8 }}>Disposition</div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(160px, 1fr))', gap:8 }}>
            {DISPOSITIONS.map(d => (
              <button key={d.code} disabled={savingDisp || gateBlocked} onClick={()=>disposition(d.code)}
                style={{
                  padding:'12px 14px', borderRadius:8, border:`1px solid ${d.color}`,
                  background: d.hot ? d.color : d.color+'22',
                  color: d.hot ? '#fff' : d.color,
                  fontWeight:700, fontSize:13, cursor: gateBlocked?'not-allowed':'pointer',
                  opacity: (savingDisp||gateBlocked)?0.6:1,
                }}>{d.label}</button>
            ))}
          </div>
        </div>

        {/* Session stats sidebar */}
        <div style={{ ...styles.card, padding:16 }}>
          <div style={{ fontSize:11, color:G.textMuted, textTransform:'uppercase', fontWeight:700, marginBottom:12 }}>Session Tally</div>
          <div style={{ display:'grid', gap:8, marginBottom:14 }}>
            {DISPOSITIONS.map(d => (
              <div key={d.code} style={{ display:'flex', justifyContent:'space-between', fontSize:13 }}>
                <span style={{ color:d.color }}>{d.label}</span>
                <strong>{dispositions?.[d.code] || 0}</strong>
              </div>
            ))}
            <div style={{ display:'flex', justifyContent:'space-between', fontSize:13, color:G.textMuted, borderTop:`1px solid ${G.border}`, paddingTop:8 }}>
              <span>⏭ Skipped</span>
              <strong>{(dispositions?.skipped || 0) + (dispositions?.deferred_hours || 0)}</strong>
            </div>
          </div>
          <Btn sz="sm" onClick={skip} style={{ width:'100%' }}>⏭ Skip (no disposition)</Btn>
        </div>
      </div>
    )}
  </div>;
}
